import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import CustomTextField from "../../../../../components/common/Textfield";
import useDebounce from "../../../../../hooks/useDebounce";
import apiService from "../../../../../service/Api";
import { RootState } from "../../../../../store/Index";
import axios from "axios";

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  profile_picture_url: string | null;
}

type AssignmentRole = "scanner" | "uploader" | "both" | "other" | null;

const ROLE_OPTIONS: { value: Exclude<AssignmentRole, null>; label: string }[] = [
  { value: "scanner", label: "Physical Comments Scanner" },
  { value: "uploader", label: "Online Comments Uploader" },
  { value: "both", label: "Both" },
  { value: "other", label: "Other" },
];

interface MemoryBookAssignment {
  id: number;
  assigned_to: User;
  role?: AssignmentRole;
  memory_book?: {
    physical_comments: number | null;
    done_comments: number | null;
    uploaded_comments: number | null;
  } | null;
}

interface AssignMemoryBookModalProps {
  open: boolean;
  onClose: () => void;
  deceasedName: string;
  streamId: number;
  onAssignSuccess?: () => void;
}

export const AssignMemoryBookModal: React.FC<AssignMemoryBookModalProps> = ({
  open,
  onClose,
  deceasedName,
  streamId,
  onAssignSuccess,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const [userVal, setUserVal] = useState("");
  const [userOptions, setUserOptions] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [existingAssignments, setExistingAssignments] = useState<
    MemoryBookAssignment[]
  >([]);
  const [userRoles, setUserRoles] = useState<Record<number, AssignmentRole>>(
    {},
  );

  const [assigning, setAssigning] = useState(false);
  const [fetchingAssignment, setFetchingAssignment] = useState(false);
  const [collectedComments, setCollectedComments] = useState("");

  const debouncedUserSearch = useDebounce(userVal, 400);

  const closeAndReset = () => {
    setUserVal("");
    setUserOptions([]);
    setSelectedUsers([]);
    setExistingAssignments([]);
    setUserRoles({});
    setCollectedComments("");
    onClose();
  };

  const fetchExistingAssignment = useCallback(async () => {
    setFetchingAssignment(true);
    try {
      const response = await apiService.get<{
        results: MemoryBookAssignment[];
      }>(`/api/streams/memory-book-assignment/?stream=${streamId}`);
      const results = response.data.results || [];
      setExistingAssignments(results);
      setSelectedUsers(results.map((a) => a.assigned_to));
      setUserRoles(
        Object.fromEntries(
          results.map((a) => [a.assigned_to.id, a.role ?? null]),
        ),
      );

      const physical = results[0]?.memory_book?.physical_comments;
      setCollectedComments(physical != null ? String(physical) : "");
    } catch (err) {
      console.error("Failed to fetch existing assignment:", err);
    } finally {
      setFetchingAssignment(false);
    }
  }, [streamId]);

  useEffect(() => {
    if (open) fetchExistingAssignment();
  }, [open, fetchExistingAssignment]);

  const fetchUsers = useCallback(async (search: string) => {
    if (!search) {
      setUserOptions([]);
      return;
    }
    setUsersLoading(true);
    try {
      const users = await apiService.get<{
        count: number;
        results: User[];
        next: string | null;
        previous: string | null;
      }>("/api/authentication/users/", { search });
      setUserOptions(users.data.results || []);
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setUserOptions([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers(debouncedUserSearch);
  }, [debouncedUserSearch, fetchUsers]);

  const handleAssign = async () => {
    if (selectedUsers.length === 0) return;

    setAssigning(true);
    try {
      const existingUserIds = new Set(
        existingAssignments.map((a) => a.assigned_to.id),
      );
      const selectedUserIds = new Set(selectedUsers.map((u) => u.id));

      const newlyAddedIds = selectedUsers
        .filter((u) => !existingUserIds.has(u.id))
        .map((u) => u.id);

      const removedAssignments = existingAssignments.filter(
        (a) => !selectedUserIds.has(a.assigned_to.id),
      );

      await Promise.all(
        removedAssignments.map((a) =>
          apiService.delete(`/api/streams/memory-book-assignment/${a.id}/`),
        ),
      );
      let newAssignmentId: number | null = null;

      if (newlyAddedIds.length > 0) {
        const response = await apiService.post<
          { id: number; assignment_id: number }[]
        >(`/api/streams/memory-book-assignment/`, {
          stream_id: streamId,
          assigned_to_ids: newlyAddedIds,
          roles: Object.fromEntries(
            newlyAddedIds.map((id) => [id, userRoles[id] ?? null]),
          ),
        });
        newAssignmentId = response.data[0]?.assignment_id ?? null;
      }

      const changedRoleAssignments = existingAssignments.filter(
        (a) =>
          selectedUserIds.has(a.assigned_to.id) &&
          (a.role ?? null) !== (userRoles[a.assigned_to.id] ?? null),
      );

      await Promise.all(
        changedRoleAssignments.map((a) =>
          apiService.patch(`/api/streams/memory-book-assignment/${a.id}/`, {
            role: userRoles[a.assigned_to.id] ?? null,
          }),
        ),
      );

      const commentsValue = collectedComments
        ? parseInt(collectedComments, 10)
        : null;

      const remainingAssignmentId =
        existingAssignments.find((a) => selectedUserIds.has(a.assigned_to.id))
          ?.id ?? newAssignmentId;

      if (commentsValue !== null && remainingAssignmentId) {
        await apiService.patch(
          `/api/streams/memory-book-assignment/${remainingAssignmentId}/`,
          { physical_comments: commentsValue },
        );
      }

      toast.success(`Memory book assignees updated for ${deceasedName}!`);

      onAssignSuccess?.();
      closeAndReset();
    } catch (err) {
      console.error("Failed to update memory book assignments:", err);
      const message = axios.isAxiosError(err)
        ? (err.response?.data?.message ?? err.message)
        : err instanceof Error
          ? err.message
          : "Failed to update memory book assignments. Please try again.";
      toast.error(message);
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open={open} onClose={closeAndReset} maxWidth="sm" fullWidth>
      <DialogTitle>
        Assign Memory Book
        <IconButton
          onClick={closeAndReset}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box mt={1}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, color: "text.secondary" }}
          >
            Assigning memory book for: <strong>{deceasedName}</strong>
          </Typography>

          {fetchingAssignment ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <Box display="flex" gap={2} alignItems="flex-start">
                <Box flex={3}>
                  <Autocomplete
                    multiple
                    options={userOptions}
                    getOptionLabel={(option) =>
                      option?.id
                        ? `${option.id} - ${option.first_name} ${option.last_name}`
                        : "No Users To Display"
                    }
                    value={selectedUsers}
                    onChange={(_, newValue) => setSelectedUsers(newValue)}
                    inputValue={userVal}
                    onInputChange={(_, newInput) => setUserVal(newInput)}
                    loading={usersLoading}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <CustomTextField
                        {...params}
                        placeholder="Search users..."
                        variant="outlined"
                        helperText="Type 3 letters to search"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {usersLoading ? (
                                <CircularProgress size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const label = option?.id
                          ? `${option.id} - ${option.first_name} ${option.last_name}`
                          : "No Users To Display";
                        return (
                          <Chip
                            label={label}
                            {...getTagProps({ index })}
                            key={option.id}
                            sx={{
                              backgroundColor: reduxTheme.primary_colors[800],
                              borderRadius: 1,
                              color: reduxTheme.primary_colors[100],
                            }}
                          />
                        );
                      })
                    }
                  />
                </Box>

                <Box flex={1} minWidth={160}>
                  <CustomTextField
                    fullWidth
                    type="number"
                    label="Collected Comments"
                    placeholder="0"
                    value={collectedComments}
                    onChange={(e) => setCollectedComments(e.target.value)}
                    helperText="Physical comments (shared across assignees)"
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </Box>
              </Box>

              <List
                sx={{
                  backgroundColor: reduxTheme.primary_colors[800],
                  borderRadius: 3,
                  mt: 1,
                  maxHeight: 300,
                  overflowY: "auto",
                  minHeight: 100,
                }}
              >
                {selectedUsers.length === 0 ? (
                  <ListItem>
                    <ListItemText
                      primary="No users assigned yet."
                      sx={{ color: reduxTheme.primary_colors[100] }}
                    />
                  </ListItem>
                ) : (
                  selectedUsers.map((u) => (
                    <ListItem key={u.id}>
                      <ListItemAvatar>
                        <Avatar
                          src={u.profile_picture_url || "/placeholder.svg"}
                        />
                      </ListItemAvatar>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: reduxTheme.primary_colors[100] }}
                        >
                          {u.first_name} {u.last_name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: reduxTheme.primary_colors[300] }}
                        >
                          {u.email}
                        </Typography>
                      </Box>
                      <ListItemSecondaryAction>
                        <Select
                          size="small"
                          displayEmpty
                          value={userRoles[u.id] ?? ""}
                          onChange={(e) =>
                            setUserRoles((prev) => ({
                              ...prev,
                              [u.id]: (e.target.value || null) as AssignmentRole,
                            }))
                          }
                          sx={{
                            minWidth: 160,
                            color: reduxTheme.primary_colors[100],
                          }}
                        >
                          <MenuItem value="">No role</MenuItem>
                          {ROLE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))
                )}
              </List>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={closeAndReset}
          sx={{
            color: reduxTheme.primary_colors[100],
            backgroundColor: reduxTheme.primary_colors[800],
          }}
        >
          Cancel
        </Button>
        <Button
          disabled={selectedUsers.length === 0 || assigning}
          onClick={handleAssign}
          sx={{
            color: reduxTheme.primary_colors[900],
            backgroundColor: reduxTheme.primary_colors[400],
            "&.Mui-disabled": { opacity: 0.5 },
          }}
        >
          {existingAssignments.length > 0 ? "Update Assignees" : "Assign Book"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
